import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreationSavedSearchDrawer,
  SavedSearchDrawer,
  SearchApplied,
  useSavedSearchDateRestore,
  useSavedSearches
} from 'features/saved-searches';
import { getMatchedStaticDateRange } from 'features/saved-searches/utils';
/** API / EntityRoutes / Endpoints / EntityType */
import useDatatable, {
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import { useSearchProject } from 'hooks/searchProject';
import { useUrlParamsUpdater } from 'hooks/urlParamsUpdater';
import { useQuickViewPreview } from 'hooks/useQuickViewPreview';
import isEmpty from 'lodash/isEmpty';
import LogRocket from 'logrocket';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { userHasRoles } from 'services/Authorization';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
/** Tracking */
/** Atoms, Components and Styles */
/** Components */
import DataTable from 'UI/components/organisms/DataTable';
import FPQuickView from 'UI/components/organisms/inventoryProfiles/FPQuickView';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { backNavigateListConfig } from 'UI/constants/config';
import { PageTitles } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { LogRocketCustomEvents } from 'UI/constants/LogRocketCustomEvents';
import { Roles } from 'UI/constants/roles';
import { createCustomStaticRanges } from 'UI/utils';
import { getMinRequiredFilters } from 'UI/utils/filters';

import {
  ColumnsDefinitions,
  DateFilters,
  FiltersGroups,
  PeriodColumnBySection,
  VirtualProps
} from './columns';
import { formatResponse } from './utils';

/** @typedef {import('react-router-dom').RouteComponentProps} RouterProps */
/** @typedef {import('react-router-dom'').RouterProps} RouterProps */
const pageKey = 'joborders-list';
const numberOfActionColumns = 1;
const dateTypeFilterKey = 'dateTypeFilter';

const orderByOptions = {
  column: 'id',
  direction: 'desc'
};

const MIN_REQUIRED_FILTERS = getMinRequiredFilters();

/**
 * @param {RouterProps} props
 */
const JobOrders = ({ history, location }) => {
  useEffect(() => {
    LogRocket.track(LogRocketCustomEvents.JobOrder.ListView);
  }, []);
  const [currentUser] = useUserContext();
  const isHighLevelUser = userHasRoles(
    [
      Roles.Operations,
      Roles.DataCoordinator,
      Roles.Leadership,
      Roles.AssistantRegionalDirector,
      Roles.Coach
    ],
    currentUser
  );

  const initialPreferences = getColumnPreferences(
    pageKey,
    0,
    orderByOptions,
    ColumnsDefinitions,
    numberOfActionColumns,
    location.search
  );

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isSavedSearchesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.JobOrderSavedSearches);
  const isListPersistDateRangeEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.InventoryListPersistDateRange
  );
  const isSavedSearchesDateEnabled = checkIfFeatureFlagEnabled(FeatureFlags.SavedSearchesDate);

  const {
    activeSearch,
    appliedPeriod,
    buildSavedSearchPayload,
    getRestoredDateTypeFilter,
    getSavedSearchButtonDefinitions,
    handleApplySearch: applySearch,
    handleCloseSavedSearchDrawer,
    handleCloseSavedSearchForm,
    handleDeleteSearch,
    handleEditSavedSearchDrawerOpen,
    handleEditSearch,
    handleRemoveSearch,
    isDirty,
    savedDateTypeId,
    savedSearchesUiState,
    savedSearchRestoreKey,
    shouldUseSavedSearchDates
  } = useSavedSearches({
    defaultDateTypeFilterId: isSavedSearchesDateEnabled ? DateFilters[0].id : null,
    isDateEnabled: isSavedSearchesDateEnabled,
    preferencesKey: pageKey
  });

  const {
    activeSection,
    columnPreferences,
    count,
    data,
    filters,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersApply,
    handleFiltersChange,
    handleFiltersToggle,
    handleKeywordChange,
    handleOneFilterChange,
    handlePageChange,
    handlePeriodChange: handleDataTablePeriodChange,
    handlePerPageChange,
    handleSectionChange,
    isBelowMinFiltersThreshold,
    listState,
    mainOptions,
    queryParams,
    selectedPeriod
  } = useDatatable({
    columnsDefinitions: ColumnsDefinitions,
    enableSavedSearches: isSavedSearchesEnabled && savedSearchesUiState.isSearching,
    endpoint: Endpoints.JobOrders,
    entityName: 'Job Orders',
    entityType: EntityType.Joborder,
    formatResponse,
    initialPreferences,
    initialSection: isSavedSearchesDateEnabled ? savedDateTypeId : DateFilters[0].id,
    key: pageKey,
    minRequiredFilters: !isHighLevelUser ? MIN_REQUIRED_FILTERS : null,
    numberOfActionColumns,
    orderByOptions,
    periodDefaultValue: shouldUseSavedSearchDates ? appliedPeriod : null,
    periodFilterColumn: PeriodColumnBySection,
    savedSearch: isSavedSearchesEnabled && activeSearch && activeSearch?.search,
    sendDateWithTimezone: true,
    shouldSaveDateRangePreferences: isListPersistDateRangeEnabled && !shouldUseSavedSearchDates,
    shouldScrollOnNavigate: true,
    virtualProps: VirtualProps
  });

  const defaultDateTypeFilter =
    appliedPeriod?.dateTypeFilter ||
    DateFilters.find(item => item.id === activeSection) ||
    DateFilters[0];

  const [dateTypeFilter, setDateTypeFilter] = useState(defaultDateTypeFilter);

  const currentSavedSearchPayload = buildSavedSearchPayload({
    dateTypeFilter,
    filters,
    selectedPeriod
  });

  const restoredDateTypeFilter = getRestoredDateTypeFilter({
    dateFilters: DateFilters,
    fallbackDateFilter: DateFilters[0]
  });

  useEffect(() => {
    handleEditSearch(
      filters,
      shouldUseSavedSearchDates ? selectedPeriod : null,
      shouldUseSavedSearchDates ? dateTypeFilter?.id : null
    );
  }, [
    activeSearch,
    dateTypeFilter,
    filters,
    handleEditSearch,
    isSavedSearchesDateEnabled,
    selectedPeriod,
    shouldUseSavedSearchDates
  ]);

  const { resetRestoreKey } = useSavedSearchDateRestore({
    activeSection,
    appliedPeriod,
    dateTypeFilter,
    handleDataTablePeriodChange,
    handleSectionChange,
    isSavedSearchesDateEnabled,
    restoredDateTypeFilter,
    savedSearchRestoreKey,
    selectedPeriod,
    setDateTypeFilter
  });

  const handleApplySearch = useCallback(
    params => {
      resetRestoreKey();
      applySearch(params);
    },
    [applySearch, resetRestoreKey]
  );

  const { columns, columnOrder, orderBy, direction } = columnPreferences;
  useUrlParamsUpdater({ history, location, orderBy });

  const { isLoading, showWholeSkeleton, isSideMenuOpen, page, perPage, keyword, userFilter } =
    listState;

  const dataForSelection = useMemo(
    () => data.map(item => ({ id: item.id, type: 'Job Order', type_id: 2 })),
    [data]
  );

  const {
    handleRowSelection,
    selectedIndexesInPage,
    multiSelectComponents,
    totalSelected,
    filteredItems
  } = useMultipleSelection({ data: dataForSelection, count, perPage });

  const { SearchProjectAction, SearchProjectForms } = useSearchProject({
    filteredItems,
    queryParams
  });

  useEffect(() => {
    document.title = PageTitles.JobOrder;
  }, []);

  const { entityId, quickViewState, listViewPreset, quickViewEntity, toggleQuickViewPreview } =
    useQuickViewPreview({ columns, data, entityType: EntityType.Joborder });

  const tableActions = buildRowActionsRendererDefinition({
    actions: [listViewPreset],
    namespace: EntityType.Joborder
  });

  const handleDateTypeFilterChange = (name, value) => {
    setDateTypeFilter(value);
    handleSectionChange && handleSectionChange(name, value.id);
  };

  const finalColumns = getColumnsToRender([...columns, tableActions]);

  const handleTabRefresh = async () => {
    await getData();
  };

  const shouldSelectRequiredFilters = isBelowMinFiltersThreshold && (!data || data.length === 0);
  const savedSearchButtonDefinitions = getSavedSearchButtonDefinitions({ filters });

  const handlePeriodChange = useCallback(
    (newPeriod, isFromStaticPeriod) => {
      const period = newPeriod ? { ...newPeriod, isFromStaticPeriod } : null;
      const matchedStaticDateRange = isFromStaticPeriod ? getMatchedStaticDateRange(period) : null;
      handleDataTablePeriodChange(
        newPeriod ? { ...period, staticDateRangeId: matchedStaticDateRange?.id || null } : null
      );
    },
    [handleDataTablePeriodChange]
  );

  const clearSavedSearchPeriod = () => {
    handleDataTablePeriodChange(null);
    handleDateTypeFilterChange(dateTypeFilterKey, DateFilters[0]);
  };

  const handleSavedSearchRemove = () => {
    const onClearPeriod = isSavedSearchesDateEnabled ? clearSavedSearchPeriod : null;
    handleRemoveSearch({ onClearPeriod });
  };

  const handleSavedSearchDelete = params => {
    const onClearPeriod = isSavedSearchesDateEnabled ? clearSavedSearchPeriod : null;
    handleDeleteSearch({ ...params, onClearPeriod });
  };

  return (
    <FiltersLayout
      title="Job Orders"
      section={EntityType.Joborder}
      listSelector={
        <AutocompleteSelect
          name="userFilter"
          placeholder="Job Orders to show"
          selectedValue={userFilter || mainOptions[0]}
          onSelect={handleOneFilterChange}
          defaultOptions={mainOptions}
          disableClearable
        />
      }
      defaultRange={selectedPeriod || null}
      extraSelector={
        <AutocompleteSelect
          name={dateTypeFilterKey}
          placeholder="Filter By"
          selectedValue={dateTypeFilter}
          onSelect={handleDateTypeFilterChange}
          defaultOptions={DateFilters}
          disableClearable
        />
      }
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      filters={filters}
      isSideMenuOpen={isSideMenuOpen}
      isLoading={showWholeSkeleton}
      isPeriodClearable
      groups={FiltersGroups}
      enableStore={false}
      onSearch={handleFiltersApply}
      onFiltersChange={handleFiltersChange}
      onMenuToggle={handleFiltersToggle}
      titleLabelProps={backNavigateListConfig}
      onPeriodChange={handlePeriodChange}
      minRequiredFilters={shouldSelectRequiredFilters ? MIN_REQUIRED_FILTERS : null}
      shouldSelectRequiredFilters={shouldSelectRequiredFilters}
      enableSavedSearches={isSavedSearchesEnabled}
      savedSearchesHeader={
        isSavedSearchesEnabled &&
        activeSearch && (
          <SearchApplied
            isDirty={isDirty}
            onRemoveSearch={handleSavedSearchRemove}
            onSaveSearch={!isEmpty(filters) ? handleEditSavedSearchDrawerOpen : null}
            searchName={activeSearch?.name}
            searchOwner={activeSearch?.owner}
          />
        )
      }
      savedSearchesActions={
        isSavedSearchesEnabled && (
          <FPButtonBuilder
            GridContainerProps={{ justifyContent: 'flex-end', spacing: 2 }}
            buttonDefinitions={savedSearchButtonDefinitions}
          />
        )
      }
    >
      <DataTable
        isExpandable
        loading={showWholeSkeleton}
        refreshing={isLoading}
        data={data}
        columns={finalColumns}
        count={count}
        filter={false}
        page={page}
        rowsPerPage={perPage}
        searchText={keyword}
        sortOrder={{ name: orderBy, direction }}
        columnOrder={columnOrder?.length ? columnOrder : undefined}
        draggableColumns={{
          enabled: true
        }}
        enableCustomFilters
        onToggleFilters={handleFiltersToggle}
        onSearchTextChange={handleKeywordChange}
        onColumnSortChange={handleColumnSortChange}
        onPerPageClick={handlePerPageChange}
        onPageClick={handlePageChange}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onRowSelectionChange={handleRowSelection}
        rowsSelected={selectedIndexesInPage}
        selectToolbarPlacement="none"
        CustomToolbar={
          <CustomTableToolbarSelect total={totalSelected}>
            <SearchProjectAction />
          </CustomTableToolbarSelect>
        }
        components={multiSelectComponents}
      />
      <SearchProjectForms />

      <When condition={entityId && quickViewState.isQuickViewOpen}>
        <FPQuickView
          drawerProps={{
            open: quickViewState.isQuickViewOpen
          }}
          entityType={quickViewEntity}
          onClose={toggleQuickViewPreview}
          itemsToNavigate={data}
          onEditionCompleted={handleTabRefresh}
          id={entityId}
        />
      </When>

      <When condition={isSavedSearchesEnabled && savedSearchesUiState.isSavedSearchDrawerOpen}>
        <SavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Joborder}
          onApplySearch={handleApplySearch}
          onClose={handleCloseSavedSearchDrawer}
          onDeleteSearch={handleSavedSearchDelete}
          open={savedSearchesUiState.isSavedSearchDrawerOpen}
        />
      </When>

      <When
        condition={
          isSavedSearchesEnabled &&
          (savedSearchesUiState.isCreationSavedSearchDrawerOpen ||
            savedSearchesUiState.isEditSavedSearchDrawerOpen)
        }
      >
        <CreationSavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Joborder}
          onApplySearch={handleApplySearch}
          onSaveCompleted={handleCloseSavedSearchForm}
          onClose={handleCloseSavedSearchForm}
          open
          savedSearchId={savedSearchesUiState.isEditSavedSearchDrawerOpen ? activeSearch?.id : null}
          search={currentSavedSearchPayload}
        />
      </When>
    </FiltersLayout>
  );
};
export default JobOrders;
